import { useState } from 'react';
import { Trash2, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  ALLOWED_CONDITIONS,
  RESERVED_OTHER_OPTION_ID,
  validateSurveyFlow,
} from '../../lib/surveyBranching';
import type {
  BranchCondition,
  BranchingRule,
  QuestionStep,
  SurveyLinkMode,
  SurveyStep,
} from '../../types/survey';
import { STEP_TYPE_LABEL, summarizeStep } from './stepSummary';

// Phase 6B B5 — branching rule editor (§3.4). Local working copy + a Save gated
// on validity: validateSurveyFlow runs live over the whole flow (this step's
// edited rules swapped in) and any error scoped to this step blocks Save and is
// shown inline. The comparator dropdown is driven by the engine's
// ALLOWED_CONDITIONS matrix; "Other…" uses RESERVED_OTHER_OPTION_ID. Targets are
// forward-only (only later steps are offered), so backward refs can't be built.

const COMPARATOR_LABEL: Record<BranchCondition['type'], string> = {
  equals: 'is',
  not_equals: 'is not',
  contains: 'includes',
  greater_than: 'is greater than',
  less_than: 'is less than',
};

function defaultCondition(step: QuestionStep): BranchCondition {
  const allowed = ALLOWED_CONDITIONS[step.type];
  const cmp = allowed[0] ?? 'equals';
  if (step.type === 'yes_no') return { type: 'equals', value: true };
  if (step.config.type === 'multiple_choice_single')
    return { type: 'equals', value: step.config.options[0]?.id ?? '' };
  if (step.config.type === 'multiple_choice_multi')
    return { type: 'contains', value: step.config.options[0]?.id ?? '' };
  if (step.type === 'rating_scale' || step.type === 'number')
    return { type: cmp, value: 0 } as BranchCondition;
  return { type: cmp, value: '' } as BranchCondition;
}

export function BranchingEditor({
  step,
  allSteps,
  linkMode,
  onSave,
  onClose,
}: {
  step: QuestionStep;
  allSteps: SurveyStep[];
  linkMode: SurveyLinkMode;
  onSave: (next: QuestionStep) => void;
  onClose: () => void;
}) {
  const [rules, setRules] = useState<BranchingRule[]>(step.branching);

  const workingStep: QuestionStep = { ...step, branching: rules };
  const editedSteps = allSteps.map((s) => (s.id === step.id ? workingStep : s));
  const errors = validateSurveyFlow(editedSteps, linkMode).filter((e) => e.stepId === step.id);

  const index = allSteps.findIndex((s) => s.id === step.id);
  const laterSteps = index >= 0 ? allSteps.slice(index + 1) : [];
  const comparators = ALLOWED_CONDITIONS[step.type];

  const addRule = () =>
    setRules((r) => [
      ...r,
      { id: crypto.randomUUID(), condition: defaultCondition(step), action: 'skip_to_outro' },
    ]);
  const updateRule = (id: string, patch: Partial<BranchingRule>) =>
    setRules((r) => r.map((rule) => (rule.id === id ? { ...rule, ...patch } : rule)));
  const removeRule = (id: string) => setRules((r) => r.filter((rule) => rule.id !== id));

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-base font-semibold text-slate-800">Branching</h3>
          <button type="button" onClick={onClose} className="rounded p-1 text-slate-400 hover:bg-slate-100" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          <p className="text-sm text-slate-500">
            For <span className="font-medium text-slate-700">{summarizeStep(step)}</span>. Rules are
            checked in order; the first match wins. With no rule, the survey continues to the next step.
          </p>

          {rules.length === 0 && (
            <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-500">No rules — linear flow.</p>
          )}

          {rules.map((rule) => (
            <RuleRow
              key={rule.id}
              step={step}
              rule={rule}
              comparators={comparators}
              laterSteps={laterSteps}
              onChange={(patch) => updateRule(rule.id, patch)}
              onRemove={() => removeRule(rule.id)}
            />
          ))}

          <Button variant="outline" size="sm" onClick={addRule}>
            + Add rule
          </Button>

          {errors.length > 0 && (
            <ul className="mt-2 space-y-1 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {errors.map((e, i) => (
                <li key={`${e.ruleId ?? 'step'}-${i}`}>{e.message}</li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSave(workingStep)} disabled={errors.length > 0}>
            Save branching
          </Button>
        </div>
      </div>
    </div>
  );
}

function RuleRow({
  step,
  rule,
  comparators,
  laterSteps,
  onChange,
  onRemove,
}: {
  step: QuestionStep;
  rule: BranchingRule;
  comparators: BranchCondition['type'][];
  laterSteps: SurveyStep[];
  onChange: (patch: Partial<BranchingRule>) => void;
  onRemove: () => void;
}) {
  const setComparator = (type: BranchCondition['type']) =>
    onChange({ condition: { type, value: rule.condition.value } as BranchCondition });
  const setValue = (value: string | number | boolean) =>
    onChange({ condition: { type: rule.condition.type, value } as BranchCondition });

  return (
    <div className="rounded-md border border-slate-200 p-3">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-500">If answer</span>
        <select
          value={rule.condition.type}
          onChange={(e) => setComparator(e.target.value as BranchCondition['type'])}
          className="rounded-md border border-slate-300 px-2 py-1 text-sm"
        >
          {comparators.map((c) => (
            <option key={c} value={c}>
              {COMPARATOR_LABEL[c]}
            </option>
          ))}
        </select>
        <ValueControl step={step} value={rule.condition.value} onChange={setValue} />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-500">then</span>
        <select
          value={rule.action}
          onChange={(e) =>
            onChange(
              e.target.value === 'skip_to_outro'
                ? { action: 'skip_to_outro', targetStepId: undefined }
                : { action: 'go_to' },
            )
          }
          className="rounded-md border border-slate-300 px-2 py-1 text-sm"
        >
          <option value="go_to">Go to question</option>
          <option value="skip_to_outro">Skip to end (outro)</option>
        </select>
        {rule.action === 'go_to' && (
          <select
            value={rule.targetStepId ?? ''}
            onChange={(e) => onChange({ targetStepId: e.target.value || undefined })}
            className="max-w-[16rem] rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">Choose a step…</option>
            {laterSteps.map((s) => (
              <option key={s.id} value={s.id}>
                {STEP_TYPE_LABEL[s.type]} — {summarizeStep(s)}
              </option>
            ))}
          </select>
        )}
        <button type="button" onClick={onRemove} className="ml-auto rounded p-1 text-red-400 hover:bg-red-50" aria-label="Remove rule">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Type-appropriate value control for the condition (drives the engine's value
// type: boolean / optionId / number / text).
function ValueControl({
  step,
  value,
  onChange,
}: {
  step: QuestionStep;
  value: string | number | boolean;
  onChange: (v: string | number | boolean) => void;
}) {
  const selectClass = 'rounded-md border border-slate-300 px-2 py-1 text-sm';
  const inputClass = 'w-28 rounded-md border border-slate-300 px-2 py-1 text-sm';

  if (step.type === 'yes_no') {
    return (
      <select value={String(value)} onChange={(e) => onChange(e.target.value === 'true')} className={selectClass}>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  if (step.config.type === 'multiple_choice_single' || step.config.type === 'multiple_choice_multi') {
    const opts = step.config.options;
    const allowOther = step.config.allowOther && step.config.type === 'multiple_choice_single';
    return (
      <select value={String(value)} onChange={(e) => onChange(e.target.value)} className={selectClass}>
        {opts.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label || '(unlabeled)'}
          </option>
        ))}
        {allowOther && <option value={RESERVED_OTHER_OPTION_ID}>Other…</option>}
      </select>
    );
  }
  if (step.type === 'rating_scale' || step.type === 'number') {
    return (
      <input
        type="number"
        value={typeof value === 'number' ? value : ''}
        onChange={(e) => onChange(e.target.value === '' ? 0 : Number(e.target.value))}
        className={inputClass}
      />
    );
  }
  // short_text / long_text
  return (
    <input
      type="text"
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="value"
      className="w-40 rounded-md border border-slate-300 px-2 py-1 text-sm"
    />
  );
}
